import {
  AUTH_ERROR_MESSAGES,
  CHANTIERCARRE_CONTACT_URL,
} from "@etudescarre/constants";
import { Button, Modal } from "@etudescarre/ui";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { authClient } from "@/lib/auth-client";

type Props = {
  open: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  defaultEmail?: string;
};

type FormData = { email: string };

export function ForgotPasswordModal({
  open,
  onOpenChange,
  defaultEmail = "",
}: Props) {
  const [step, setStep] = useState<"form" | "sent">("form");
  const [error, setError] = useState("");
  const [showContactLink, setShowContactLink] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, reset } = useForm<FormData>({
    defaultValues: { email: defaultEmail },
  });

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setStep("form");
        setError("");
        setShowContactLink(false);
        reset({ email: defaultEmail });
      }, 200);
      return () => clearTimeout(timer);
    }
    reset({ email: defaultEmail });
  }, [open, defaultEmail, reset]);

  const clearError = () => {
    setError("");
    setShowContactLink(false);
  };

  const handleSubmitForm = async (data: FormData) => {
    clearError();
    setIsLoading(true);

    try {
      const result = await authClient.requestPasswordReset({
        email: data.email,
        fetchOptions: { throw: false },
      });

      if (result.error) {
        if (result.error.message === AUTH_ERROR_MESSAGES.EMAIL_NOT_FOUND) {
          setError("Cette adresse n'est associée à aucun utilisateur.");
          setShowContactLink(true);
        } else if (result.error.status === 429) {
          setError(
            "Trop de tentatives. Veuillez réessayer dans quelques instants.",
          );
        } else {
          setError("Une erreur est survenue. Veuillez réessayer.");
        }
        return;
      }

      setStep("sent");
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  const emailRegister = register("email");

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} lazyMount unmountOnExit>
      <Modal.Portal>
        <Modal.Backdrop />
        <Modal.Positioner>
          <Modal.Content size="sm">
            <Modal.Header>
              <Modal.Title>
                {step === "form"
                  ? "Renouveler mon mot de passe"
                  : "E-mail envoyé"}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {step === "form" ? (
                <form
                  onSubmit={handleSubmit(handleSubmitForm)}
                  className="space-y-4"
                >
                  <p className="text-gray-600 text-sm">
                    Saisissez votre adresse e-mail pour recevoir un lien de
                    réinitialisation.
                  </p>

                  <div>
                    <label
                      className="mb-2 block font-medium text-gray-700 text-sm uppercase"
                      htmlFor="reset-email"
                    >
                      Adresse e-mail
                    </label>
                    <input
                      {...emailRegister}
                      onChange={(e) => {
                        emailRegister.onChange(e);
                        if (error) clearError();
                      }}
                      onInvalid={(e) => {
                        e.preventDefault();
                        setError("Cette adresse n'est pas valide.");
                      }}
                      autoComplete="email"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-[#1a3b5c] focus:outline-none focus:ring-1 focus:ring-[#1a3b5c]"
                      id="reset-email"
                      placeholder="votre@email.com"
                      required
                      pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                      type="email"
                    />
                  </div>

                  {error && (
                    <div
                      className="rounded-md bg-red-50 p-3"
                      role="alert"
                      aria-live="assertive"
                    >
                      <p className="text-red-800 text-sm">{error}</p>
                      {showContactLink && (
                        <a
                          href={CHANTIERCARRE_CONTACT_URL}
                          className="mt-2 block text-sm font-medium text-red-700 underline hover:text-red-900"
                          target="chantiercarre"
                          rel="noopener noreferrer"
                        >
                          Formulaire de contact
                        </a>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => onOpenChange({ open: false })}
                      className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#1a3b5c]"
                    >
                      Annuler
                    </button>
                    <Button
                      className="flex-1 bg-[#1a3b5c] hover:bg-[#2a4b6c]"
                      disabled={isLoading}
                      type="submit"
                    >
                      {isLoading ? "Envoi..." : "Envoyer"}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4 text-center py-2">
                  <div className="flex justify-center">
                    <CheckCircleIcon className="h-12 w-12 text-green-500" />
                  </div>
                  <p className="text-gray-700 text-sm">
                    Un e-mail vous a été envoyé. Vérifiez votre boîte de
                    réception et suivez le lien pour créer votre nouveau mot de
                    passe.
                  </p>
                  <p className="text-gray-400 text-xs">
                    Le lien est valable 15 minutes.
                  </p>
                  <Button
                    className="w-full bg-[#1a3b5c] hover:bg-[#2a4b6c]"
                    type="button"
                    onClick={() => onOpenChange({ open: false })}
                  >
                    Fermer
                  </Button>
                </div>
              )}
            </Modal.Body>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Portal>
    </Modal.Root>
  );
}
